import moment from 'moment';
import { Attr, HasMany, HasOne, Model } from 'spraypaint';
import {
  Airline,
  Customer,
  Discount,
  Extra,
  Lot,
  LotRegion,
  User,
  Vehicle,
  VehicleType,
} from './';
import ApplicationRecord from './ApplicationRecord';

@Model()
export default class Booking extends ApplicationRecord {
  static jsonapiType = 'bookings';

  static listHeaders() {
    return [
      {
        text: 'Booking Date',
        value: 'created',
        sortable: true,
        width: 140,
      },
      {
        text: 'Type',
        value: 'bookingType',
        sortable: false,
      },
      {
        text: 'Customer',
        value: 'customer',
        sortable: false,
      },
      {
        text: 'Vehicle',
        value: 'rego',
        sortable: false,
      },
      {
        text: 'Departure',
        value: 'startDatetime',
      },
      {
        text: 'Arrival',
        value: 'endDatetime',
      },
      {
        text: 'Cost',
        value: 'totalCost',
      },
      {
        text: 'Status',
        value: 'status',
        width: 80,
        align: 'center',
        sortable: false,
      },
      {
        text: 'Paid',
        value: 'paymentStatus',
        width: 90,
        align: 'center',
        sortable: true,
      },
    ];
  }

  static scopeFactory() {
    return Booking.includes([]);
  }

  static defaultOptions() {
    return {
      sortBy: ['created'],
      sortDesc: [true],
    };
  }

  @Attr() title: string;
  @Attr() firstName: string;
  @Attr() lastName: string;
  @Attr() phoneNumber: string;
  @Attr() email: string;
  @Attr() business: string;
  @Attr() publicNotes: string;
  @Attr() notes: string;
  @Attr() rego: string;
  @Attr() make: string;
  @Attr() model: string;
  @Attr() colour: string;
  @Attr() fuelLevel: string;
  @Attr() damage: string;
  @Attr() timeParked: string;
  @Attr() vehicleNotes: string;
  @Attr() kilometers: number;
  @Attr() created: string;
  @Attr() startDatetime: string;
  @Attr() endDatetime: string | null;
  @Attr() busDepartureTime: string | null;
  @Attr() passengersOut: number = 1;
  @Attr() passengersIn: number;
  @Attr() flightNumberOut: string;
  @Attr() flightNumberIn: string;
  @Attr() lotRegionRow: number;
  @Attr() bookingType: string = 'New';
  @Attr() promoCode: string;
  @Attr() extrasPrices: { [id: string]: string };
  @Attr() parkingCost: string = '0';
  @Attr() extrasCost: string = '0';
  @Attr() discountRate: string = '0';
  @Attr() amountDiscounted: string = '0';
  @Attr() subscriptionDiscount: string = '0';
  @Attr() totalCost: string = '0';
  @Attr() amountPaid: string;
  @Attr() paymentMethod: string;
  @Attr() paymentStatus: string = 'unpaid';
  @Attr() paymentDatetime: string;
  @Attr() status: string;
  @Attr() readonly clientSecret: string;
  @Attr() readonly stripeComplete: string;
  @Attr() readonly bookingCode: string;
  @Attr() readonly hadSubscription: boolean;

  @HasOne() parkedBy: User | null;
  @HasOne() vehicleType: VehicleType;
  @HasOne() airlineOut: Airline;
  @HasOne() airlineIn: Airline | null;
  @HasOne() lot: Lot;
  @HasOne() lotRegion: LotRegion;
  @HasOne() customer: Customer;
  @HasOne() vehicle: Vehicle;
  @HasMany() extras: Extra[];
  @HasOne() createdBy: User;
  @HasOne() discount: Discount;

  get fullName() {
    return `${this.firstName} ${this.lastName}`;
  }

  get formattedDate() {
    return this.created ? moment(this.created).format('DD/MM/YYYY') : '';
  }

  get startFormatted() {
    const dateTime = this.startDatetime
      ? moment(this.startDatetime).format('DD/MM/YYYY HH:mm')
      : '';
    const flight = this.flightNumberOut || '';
    return `${dateTime}<br>${flight}`;
  }

  get endFormatted() {
    const dateTime = this.endDatetime
      ? moment(this.endDatetime).format('DD/MM/YYYY HH:mm')
      : '';
    const flight = this.flightNumberIn || '';
    return `${dateTime}<br>${flight}`;
  }

  get totalCostFormatted() {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
    }).format(Number(this.totalCost));
  }

  get amountPaidFormatted() {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
    }).format(Number(this.amountPaid));
  }

  get statusColour() {
    switch (this.status) {
      case 'complete':
        return 'green';
      case 'pending':
        return 'yellow';
      default:
        return 'red';
    }
  }

  get paidColour() {
    switch (this.paymentStatus) {
      case 'paid':
        return 'green';
      case 'refunded':
        return 'red';
      default:
        return 'grey';
    }
  }
}
